import { useMutation, useQuery } from 'react-query';
import { ICredit } from '../../interfaces/credit.interface';
import { UpdateCreditPayload } from '../../interfaces/credit-payload.interface';
import { baseApi, buildAuthHeaders, useRetrieveToken } from './shared';

export const useCreditApi = () => {
  const { retrieveToken, isAuthenticated } = useRetrieveToken();

  const useCreditByUserId = (userId: string) => {
    return useQuery<unknown, unknown, ICredit>(
      ['credit', userId],
      async ({ signal }) => {
        const token = await retrieveToken('getCredit');
        if (!token) return {} as ICredit;

        const response = await fetch(`${baseApi}/credit/${userId}`, {
          method: 'GET',
          headers: buildAuthHeaders(token),
          signal,
        });

        if (!response.ok) throw new Error('Failed to fetch credit');

        const data = await response.json();
        return data as ICredit;
      },
      { refetchOnWindowFocus: false, enabled: !!userId && isAuthenticated },
    );
  };

  const createOrUpdateCredit = useMutation(
    async (payload: ICredit & { customerName: string; email: string }) => {
      const token = await retrieveToken('createOrUpdateCredit');
      if (!token) return {} as ICredit;

      const response = await fetch(`${baseApi}/credit/debt-limit`, {
        method: 'POST',
        headers: buildAuthHeaders(token),
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Failed to create/update credit');

      const data = await response.json();
      return data as ICredit;
    },
  );

  const useUpdateCreditAmountUsed = () =>
    useMutation(async ({ userId, amountToAdd, customerName, email }: UpdateCreditPayload) => {
      const token = await retrieveToken('updateCreditAmountUsed');
      if (!token) throw new Error('Missing auth token');

      const response = await fetch(`${baseApi}/credit/${userId}`, {
        method: 'PATCH',
        headers: buildAuthHeaders(token),
        body: JSON.stringify({ amountToAdd, customerName, email }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to update credit properly');
      }

      return data.credit as ICredit;
    });

  return {
    useCreditByUserId,
    createOrUpdateCredit,
    useUpdateCreditAmountUsed,
  };
};
